/** Course portfolio section — filterable list of enrolled courses with search and sort controls. */
import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { EnrolmentWithCourse } from '@/shared/api/hooks/useEnrolments';
import { CourseCap } from '@/shared/graphics';
import CourseRow from './CourseRow';

type SortKey = 'recent' | 'name' | 'enrolled';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'recent', label: 'Recent' },
  { key: 'name', label: 'Name' },
  { key: 'enrolled', label: 'Enrolled' },
];

function sortEnrolments(
  enrolments: EnrolmentWithCourse[],
  sortKey: SortKey
): EnrolmentWithCourse[] {
  // Always partition: active first, completed last
  const active: EnrolmentWithCourse[] = [];
  const completed: EnrolmentWithCourse[] = [];

  for (const e of enrolments) {
    if (e.completedAt) {
      completed.push(e);
    } else {
      active.push(e);
    }
  }

  const compareFn = (a: EnrolmentWithCourse, b: EnrolmentWithCourse) => {
    switch (sortKey) {
      case 'recent': {
        const aTime = a.lastAccessedAt ? new Date(a.lastAccessedAt).getTime() : 0;
        const bTime = b.lastAccessedAt ? new Date(b.lastAccessedAt).getTime() : 0;
        return bTime - aTime;
      }
      case 'name':
        return a.courses.title.localeCompare(b.courses.title);
      case 'enrolled': {
        const aTime = a.enrolledAt ? new Date(a.enrolledAt).getTime() : 0;
        const bTime = b.enrolledAt ? new Date(b.enrolledAt).getTime() : 0;
        return bTime - aTime;
      }
    }
  };

  active.sort(compareFn);
  completed.sort(compareFn);
  return [...active, ...completed];
}

interface CoursePortfolioProps {
  enrolments: EnrolmentWithCourse[];
}

/**
 * Course portfolio — compact list with search + sort controls.
 * Completed courses always sink to the bottom within the chosen sort.
 */
export default function CoursePortfolio({ enrolments }: CoursePortfolioProps) {
  const [sortKey, setSortKey] = useState<SortKey>('recent');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return enrolments;
    const q = search.trim().toLowerCase();
    return enrolments.filter((e) => e.courses.title.toLowerCase().includes(q));
  }, [enrolments, search]);

  const sorted = useMemo(() => sortEnrolments(filtered, sortKey), [filtered, sortKey]);

  if (enrolments.length === 0) return null;

  return (
    <section>
      {/* Header row — title + controls */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <h3
          className="flex items-center gap-2 font-semibold"
          style={{
            fontSize: 'var(--text-base)',
            color: 'var(--color-text)',
            fontFamily: 'var(--font-display)',
          }}
        >
          <CourseCap size={26} />
          Your Courses
        </h3>

        {/* Push controls to the right */}
        <div className="ml-auto flex items-center gap-3">
          {/* Sort pills */}
          <div className="flex items-center gap-1">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSortKey(opt.key)}
                className="rounded-full px-2.5 py-1 transition-all duration-150"
                style={{
                  fontSize: 'var(--text-xs)',
                  fontWeight: sortKey === opt.key ? 600 : 400,
                  color:
                    sortKey === opt.key ? 'var(--color-accent-text)' : 'var(--color-text-muted)',
                  background: sortKey === opt.key ? 'var(--color-accent-subtle)' : 'transparent',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Search input */}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="rounded-full outline-none transition-all duration-200 placeholder:transition-colors placeholder:duration-200 focus:ring-1"
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text)',
              background: 'var(--color-surface-muted)',
              border: '1px solid var(--color-border)',
              padding: '5px 12px',
              width: 120,
              // @ts-expect-error -- CSS custom property for focus ring
              '--tw-ring-color': 'var(--color-accent)',
            }}
          />
        </div>
      </div>

      {/* Course list */}
      <div className="mt-4 flex flex-col">
        <AnimatePresence mode="popLayout">
          {sorted.map((enrolment, index) => (
            <motion.div
              key={enrolment.id}
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{
                duration: 0.35,
                delay: index * 0.04,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <CourseRow enrolment={enrolment} />
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Empty search state */}
        {search.trim() && sorted.length === 0 && (
          <p
            className="py-8 text-center"
            style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}
          >
            No courses match &ldquo;{search.trim()}&rdquo;
          </p>
        )}
      </div>
    </section>
  );
}
